/**
 * Markdown rendering for agent messages. Escapes raw HTML, drops images, relativizes ycharts.com links,
 * colors signed percentages, and rebuilds tables in the site's dense-table markup.
 */
export const AGENT_MARKDOWN_RENDERER_OPTIONS = {
    html: (html) => {
        // Escape raw HTML so injected markup (e.g. <img> beacons from prompt injection) is
        // displayed as text instead of rendered
        return html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },
    image: (_href, _title, text) => {
        // Render images as their alt text only, so external image requests (which can act as
        // network beacons) are never triggered
        return text;
    },
    link: (href, _title, text) => {
        // Remove the domain from links so that they always link to the current environment. The lookahead
        // keeps the match anchored to the host so e.g. ycharts.com.evil.example is left alone.
        const cleanedHref = href.replace(/^https?:\/\/ycharts\.com(?::\d+)?(?=\/|$|\?|#)/, '');
        // Make links easier to see and always open them in a new tab
        return `<a href="${cleanedHref}" class="text-underline text-blue" target="_blank">${text}</a>`;
    },
    text: (text) => {
        // Add colors for percentage change values
        const percentageRegex = /(^|\(|\s)([-+]?)(\d+(?:,\d{3})*(?:\.\d+)?)%/g;
        return text.replace(percentageRegex, (fullMatch, lead, sign, num) => {
            const percentage = `${sign ?? ''}${num}%`;
            if (sign === '-') {
                return `${lead}<span class="text-red">${percentage}</span>`;
            }
            if (sign === '+') {
                return `${lead}<span class="text-green">${percentage}</span>`;
            }
            return fullMatch;
        });
    },
    table: (header, body) => {
        const tpl = document.createElement('template');
        tpl.innerHTML = `<table><thead>${header}</thead><tbody>${body}</tbody></table>`;
        const tableEl = tpl.content.querySelector('table');
        const thead = tableEl.querySelector('thead');
        const tbody = tableEl.querySelector('tbody');
        // Build Header
        const headerRow = thead.querySelector('tr');
        if (headerRow) {
            const headerCells = Array.from(headerRow.children);
            headerCells.forEach((origEl, idx) => {
                const rawText = (origEl.textContent ?? '').trim();
                const td = document.createElement('td');
                // Build the cell with DOM APIs so header text is never reinterpreted as HTML
                if (idx === 0) {
                    td.className = 'table-dynamic-name';
                    const label = document.createElement('div');
                    label.className = 'form-label form-label-thin';
                    label.textContent = rawText;
                    td.appendChild(label);
                }
                else {
                    td.className = 'text-left colTxtShort';
                    if (rawText) {
                        const strong = document.createElement('strong');
                        strong.textContent = rawText;
                        td.appendChild(strong);
                    }
                }
                origEl.replaceWith(td);
            });
        }
        // Build Body
        Array.from(tbody.querySelectorAll('tr')).forEach((trEl) => {
            const tds = Array.from(trEl.children);
            tds.forEach((origEl, idx) => {
                const newTd = document.createElement('td');
                const content = origEl.innerHTML;
                if (idx === 0) {
                    newTd.className = 'table-dynamic-name';
                }
                else {
                    const existing = origEl.getAttribute('class');
                    newTd.setAttribute('class', existing ? `text-left ${existing}` : 'text-left');
                }
                newTd.innerHTML = content;
                origEl.replaceWith(newTd);
            });
        });
        return `
            <div class="panel-data">
              <div class="panel-content">
                <div class="company-financials-table-wrap">
                  <div class="ai-chat-table-section company-financials-table">
                    <table class="table table-dense table-striped">
                      ${thead.outerHTML}
                      ${tbody.outerHTML}
                    </table>
                  </div>
                </div>
              </div>
            </div>
        `;
    },
};
export const INCOMPLETE_MARKDOWN_LINK_REGEX = /\[[^\]]*\]\([^)]*$|\[[^\]]*$/g;
